import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Stack } from "@mui/material";
import { createPortal } from "react-dom";
import { JobTitleItem } from "../products/CreateCampaignFlow/steps/DefineTargetJobTitlesStep/JobTitleItem";
import { JobTitle } from "../../types/apiTypes";
import { useEffect, useState } from "react";
import { useScrollWrapper } from "../../hooks";
import { toast } from "react-toastify";
import { CustomToast } from "../ui/CustomToast";
import { NAME_REGEX } from "../../utils/regex";

type Props = {
  titles: JobTitle[];
  setTitles: React.Dispatch<React.SetStateAction<JobTitle[]>>;
  searchTitleId?: string;
  createdTitleId: string;
  setCreatedTitleId: React.Dispatch<React.SetStateAction<string>>;
};

export const JobTitlesSortableList = ({
  titles,
  setTitles,
  searchTitleId,
  createdTitleId,
  setCreatedTitleId,
}: Props) => {
  const [activeItem, setActiveItem] = useState<string>("");
  const { scrollRef } = useScrollWrapper({ trigger: titles });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: any) => {
    setActiveItem(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveItem("");

    if (over && active.id !== over.id) {
      setTitles((prevItems) => {
        const oldIndex = prevItems.findIndex((i) => i.id === active.id);
        const newIndex = prevItems.findIndex((i) => i.id === over.id);
        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  };

  const changeHandler = (item: JobTitle) => {
    const { id, title } = item;
    const trimmedTitle = title.trim();

    if (
      titles
        .filter((el) => el.id !== id)
        .some((el) => el.title.toLowerCase() === trimmedTitle.toLowerCase())
    ) {
      toast.error(
        <CustomToast
          type="error"
          title="Job Title Update Failed"
          text="Each job title must be unique. Please choose a different title."
        />,
      );
      return;
    }

    if (
      !trimmedTitle ||
      !NAME_REGEX.test(trimmedTitle) ||
      trimmedTitle.length < 3 ||
      trimmedTitle.length > 100
    ) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid Job Title"
          text="Must contain only letters, numbers, and basic punctuation.
             Please enter a valid job title using 3 to 100 characters."
        />,
      );
      return;
    }

    setTitles((state) =>
      state.map((el) => (el.id === id ? { ...item, title: trimmedTitle } : el)),
    );
  };

  const toogleCrirtical = (item: JobTitle, index: number) => {
    const updated = titles.map((title) => {
      if (title.id === item.id) {
        return { ...title, is_critical: !title.is_critical };
      } else {
        return title;
      }
    });

    const isCritical = updated[index].is_critical;

    if (isCritical) {
      setTitles(arrayMove(updated, index, 0));
    } else {
      setTitles(updated);
    }
  };

  const removeHandler = (id: string) => {
    setTitles((state) => state.filter((el) => el.id !== id));
  };

  const scrollToElementById = (id: string) => {
    const searchElement = document.getElementById(id);
    searchElement?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  useEffect(() => {
    if (searchTitleId) scrollToElementById(searchTitleId);
  }, [searchTitleId]);

  useEffect(() => {
    if (createdTitleId) {
      scrollToElementById(createdTitleId);
      setCreatedTitleId("");
    }
  }, [createdTitleId]);

  return (
    <Stack
      sx={{
        border: "1px solid #D9D9D9",
        borderRadius: "8px",
        backgroundColor: "#F9F9F9",
        flexGrow: 1,
        padding: "10px 8px 10px 16px",
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Stack
          ref={scrollRef}
          sx={{
            gap: "8px",
            maxHeight: "Calc(100vh - 500px)",
            minHeight: titles.length > 2 ? "216px" : "auto",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#aaa",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              width: "12px",
              backgroundColor: "transparent",
            },
            position: "relative",
          }}
        >
          <SortableContext
            items={titles.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {titles.map((item, index) => (
              <JobTitleItem
                toogleCrirtical={toogleCrirtical}
                removeHandler={removeHandler}
                changeHandler={changeHandler}
                key={item.id}
                item={item}
                index={index}
                searchItemId={searchTitleId}
              />
            ))}
            {createPortal(
              <DragOverlay style={{ zIndex: 999999 }}>
                {activeItem ? (
                  <JobTitleItem
                    toogleCrirtical={toogleCrirtical}
                    removeHandler={removeHandler}
                    changeHandler={changeHandler}
                    item={titles.find((i) => i.id === activeItem)!}
                    index={-1}
                    withHandle={false}
                  />
                ) : null}
              </DragOverlay>,
              document.body,
            )}
          </SortableContext>
        </Stack>
      </DndContext>
    </Stack>
  );
};
